import {
  BudgetDraft,
  BudgetDraftTotals,
  FixedCostDraftRow,
  IncomeDraftRow,
  ProjectDraftRow,
} from 'src/Components/Expenses/BudgetWizard/BudgetWizard.types';
import {
  BudgetForecastLineRequestDto,
  BudgetForecastLineRequestDtoSourceTypeEnum,
  BudgetSuggestionsDto,
  BudgetYearDto,
  BudgetYearLineDto,
} from 'src/Services/API/generated';
import {
  BudgetCategory,
  BudgetMainCategory,
  BudgetSubCategory,
  DAILY_LIFE_MAIN_CATEGORY,
  fundCallCategoryOfFrequency,
  FUND_CALL_MAIN_CATEGORY,
  WORKS_MAIN_CATEGORY,
} from 'src/Utils/budgetCategories';
import { draftAnnualAmount, roundToCents } from 'src/Utils/budgetWizardMath';

let keyCounter = 0;
export const nextDraftKey = (prefix: string): string => `${prefix}-${++keyCounter}`;

const asCategory = (line: BudgetYearLineDto): BudgetCategory | null =>
  (line.category ?? null) as BudgetCategory | null;

const asMainCategory = (line: BudgetYearLineDto): BudgetMainCategory =>
  line.mainCategory as unknown as BudgetMainCategory;

const asSubCategory = (line: BudgetYearLineDto): BudgetSubCategory | null =>
  (line.subCategory ?? null) as unknown as BudgetSubCategory | null;

/**
 * Turns what the API knows (the saved lines) and what it suggests (N−1 and the projects) into
 * the draft the wizard edits.
 *
 * A year that has never been saved starts with every suggestion pre-checked, as the brief asks.
 * A year that already has lines does not: the manager already made his choices, and silently
 * re-adding what he had removed would inflate his budget behind his back.
 */
export const buildDraft = (
  budgetYear: BudgetYearDto,
  suggestions: BudgetSuggestionsDto
): BudgetDraft => {
  const isFirstTime = budgetYear.lines.length === 0;

  const savedFixedCosts = budgetYear.lines.filter(line => line.section === 'FIXED_COSTS');
  const savedDailyLife = budgetYear.lines.filter(line => line.section === 'DAILY_LIFE');
  const savedProjects = budgetYear.lines.filter(line => line.section === 'PROJECTS');
  const savedIncomes = budgetYear.lines.filter(line => line.section === 'INCOME');

  const savedFixedCategories = new Set(savedFixedCosts.map(line => line.category ?? ''));
  const savedProjectIds = new Set(savedProjects.map(line => line.sourceProjectId ?? ''));

  const fixedCosts: FixedCostDraftRow[] = [
    ...savedFixedCosts.map(line => ({
      key: nextDraftKey('fixed'),
      category: asCategory(line),
      mainCategory: asMainCategory(line),
      subCategory: asSubCategory(line),
      amount: line.amount,
      previousYearAmount: line.previousYearAmount ?? null,
      note: line.note ?? '',
      checked: true,
      isManual: false,
    })),
    ...suggestions.fixedCosts
      .filter(suggestion => !savedFixedCategories.has(suggestion.category))
      .map(suggestion => ({
        key: nextDraftKey('fixed'),
        category: suggestion.category as unknown as BudgetCategory,
        mainCategory: suggestion.mainCategory as unknown as BudgetMainCategory,
        subCategory: suggestion.subCategory as unknown as BudgetSubCategory,
        amount: suggestion.previousYearAmount,
        previousYearAmount: suggestion.previousYearAmount,
        note: '',
        checked: isFirstTime,
        isManual: false,
      })),
  ];

  const dailyLifeAmount = roundToCents(
    savedDailyLife.reduce((sum, line) => sum + line.annualAmount, 0)
  );

  const projects: ProjectDraftRow[] = [
    ...savedProjects.map(line => ({
      key: nextDraftKey('project'),
      projectId: line.sourceProjectId ?? null,
      title:
        suggestions.projects.find(project => project.projectId === line.sourceProjectId)?.title ??
        null,
      statusLabel: null,
      category: asCategory(line),
      mainCategory: asMainCategory(line),
      subCategory: asSubCategory(line),
      amount: line.amount,
      checked: true,
      isManual: false,
    })),
    ...suggestions.projects
      .filter(project => !savedProjectIds.has(project.projectId))
      .map(project => ({
        key: nextDraftKey('project'),
        projectId: project.projectId,
        title: project.title,
        statusLabel: project.status,
        category: null,
        mainCategory: WORKS_MAIN_CATEGORY,
        subCategory: null,
        amount: project.estimatedCost ?? 0,
        // « À voter » is on its way to being decided, so it is proposed checked; an idea from
        // the « Boîte à idées » is not a commitment and stays unchecked.
        checked: isFirstTime && project.status === 'IDEA_TO_VOTE',
        isManual: false,
      })),
  ];

  const incomes: IncomeDraftRow[] = savedIncomes.map(line => ({
    key: nextDraftKey('income'),
    category: asCategory(line),
    mainCategory: asMainCategory(line),
    subCategory: asSubCategory(line),
    amount: line.amount,
    frequencyPerYear: line.frequencyPerYear,
    perHousehold: line.perHousehold,
    checked: true,
    isFundCall: line.mainCategory === 'FUNDING_CALLS',
    isManual: false,
  }));

  if (!incomes.some(income => income.isFundCall)) {
    incomes.unshift({
      key: nextDraftKey('income'),
      category: fundCallCategoryOfFrequency(12),
      mainCategory: FUND_CALL_MAIN_CATEGORY,
      subCategory: null,
      amount: 0,
      frequencyPerYear: 12,
      perHousehold: true,
      checked: true,
      isFundCall: true,
      isManual: false,
    });
  }

  return {
    fixedCosts,
    dailyLife: {
      enabled:
        savedDailyLife.length > 0 || (isFirstTime && suggestions.dailyLife.previousYearTotal > 0),
      amount: savedDailyLife.length > 0 ? dailyLifeAmount : suggestions.dailyLife.previousYearTotal,
      previousYearAmount:
        suggestions.dailyLife.previousYearTotal > 0
          ? suggestions.dailyLife.previousYearTotal
          : null,
    },
    projects,
    incomes,
  };
};

/**
 * Live totals of the draft. Mirrors the API rules on purpose — see `budgetWizardMath.ts` for
 * why the wizard cannot ask the server to total lines that are not saved yet.
 */
export const computeDraftTotals = (
  draft: BudgetDraft,
  householdCount: number
): BudgetDraftTotals => {
  const fixedTotal = draft.fixedCosts
    .filter(row => row.checked)
    .reduce((sum, row) => sum + row.amount, 0);
  const dailyLifeTotal = draft.dailyLife.enabled ? draft.dailyLife.amount : 0;
  const projectTotal = draft.projects
    .filter(row => row.checked)
    .reduce((sum, row) => sum + row.amount, 0);

  const expenseTotal = roundToCents(fixedTotal + dailyLifeTotal + projectTotal);

  const checkedIncomes = draft.incomes.filter(row => row.checked);
  const incomeTotal = roundToCents(
    checkedIncomes.reduce((sum, row) => sum + draftAnnualAmount(row, householdCount), 0)
  );
  const otherIncomeTotal = roundToCents(
    checkedIncomes
      .filter(row => !row.isFundCall)
      .reduce((sum, row) => sum + draftAnnualAmount(row, householdCount), 0)
  );

  const previousYearAmounts = [
    ...draft.fixedCosts.filter(row => row.checked).map(row => row.previousYearAmount),
    draft.dailyLife.enabled ? draft.dailyLife.previousYearAmount : null,
  ].filter((value): value is number => value != null);

  return {
    expenseTotal,
    incomeTotal,
    otherIncomeTotal,
    financingNeed: Math.max(0, roundToCents(expenseTotal - otherIncomeTotal)),
    balance: roundToCents(incomeTotal - expenseTotal),
    calculatedCap: expenseTotal,
    previousYearExpenseTotal: previousYearAmounts.length
      ? roundToCents(previousYearAmounts.reduce((sum, value) => sum + value, 0))
      : null,
  };
};

/** The payload of the replace-all `PUT`: only the checked lines of the draft are kept. */
export const draftToLines = (draft: BudgetDraft): BudgetForecastLineRequestDto[] => {
  const lines: BudgetForecastLineRequestDto[] = [];

  for (const row of draft.fixedCosts) {
    if (!row.checked) continue;
    lines.push({
      mainCategory: row.mainCategory,
      subCategory: row.subCategory ?? undefined,
      amount: Math.max(0, row.amount),
      category: row.category ?? undefined,
      frequencyPerYear: 1,
      perHousehold: false,
      previousYearAmount: row.previousYearAmount ?? undefined,
      note: row.note.trim() || undefined,
      sourceType:
        row.previousYearAmount != null
          ? BudgetForecastLineRequestDtoSourceTypeEnum.SuggestionPreviousYear
          : BudgetForecastLineRequestDtoSourceTypeEnum.Manual,
    });
  }

  if (draft.dailyLife.enabled) {
    lines.push({
      mainCategory: DAILY_LIFE_MAIN_CATEGORY,
      amount: Math.max(0, draft.dailyLife.amount),
      frequencyPerYear: 1,
      perHousehold: false,
      previousYearAmount: draft.dailyLife.previousYearAmount ?? undefined,
      sourceType:
        draft.dailyLife.previousYearAmount != null
          ? BudgetForecastLineRequestDtoSourceTypeEnum.SuggestionPreviousYear
          : BudgetForecastLineRequestDtoSourceTypeEnum.Manual,
    });
  }

  for (const row of draft.projects) {
    if (!row.checked) continue;
    lines.push({
      mainCategory: row.mainCategory,
      subCategory: row.subCategory ?? undefined,
      amount: Math.max(0, row.amount),
      category: row.category ?? undefined,
      frequencyPerYear: 1,
      perHousehold: false,
      sourceType: row.projectId
        ? BudgetForecastLineRequestDtoSourceTypeEnum.Project
        : BudgetForecastLineRequestDtoSourceTypeEnum.Manual,
      sourceProjectId: row.projectId ?? undefined,
    });
  }

  for (const row of draft.incomes) {
    if (!row.checked) continue;
    lines.push({
      mainCategory: row.mainCategory,
      subCategory: row.subCategory ?? undefined,
      amount: Math.max(0, row.amount),
      category: row.category ?? undefined,
      frequencyPerYear: row.frequencyPerYear,
      perHousehold: row.perHousehold,
      sourceType: BudgetForecastLineRequestDtoSourceTypeEnum.Manual,
    });
  }

  return lines;
};
